import { cardFullscreen } from "../../../shared/cards/CardFullscreen";
import { createVerseCardFace } from "../../../shared/cards/cardFace";
import {
  CAMPAIGNS,
  campaignById,
  countsFromIds,
  defaultCampaignId,
  isDifficultyCleared,
  isMissionUnlocked,
  isOwned,
  loadoutLabel,
  ownedCount,
  resolveMissionLoadout,
  totalCount,
  uniqueIds,
  type CampaignDef,
  type CampaignDifficulty,
  type CampaignSave,
  type MissionDef,
} from "../campaign";
import { verseById } from "../data/verses";
import { keywordsForVerse } from "../game/cards/keywords";
import type { VerseDef } from "../game/cards/types";

const DIFFS: CampaignDifficulty[] = ["easy", "standard", "hard"];
const DIFF_LABEL: Record<CampaignDifficulty, string> = {
  easy: "Easy",
  standard: "Standard",
  hard: "Hard",
};

const LAST_CAMPAIGN_KEY = "sots-proclaim-last-campaign";

type HubView = "library" | "missions";

function openVerseStudy(def: VerseDef): void {
  const speak = def.speakSeconds ?? 2.5;
  const kws = keywordsForVerse(def);
  const detailRows: { label: string; value: string }[] = [
    { label: "Speak", value: `${speak.toFixed(1)}s` },
    { label: "Kind", value: def.kind },
    { label: "Category", value: def.category },
  ];
  if (def.strongVs?.length) {
    detailRows.push({
      label: "Meets",
      value: def.strongVs
        .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
        .join(" / "),
    });
  }
  if (def.preferLayer) {
    detailRows.push({ label: "Layer", value: def.preferLayer });
  }
  if (def.lockLayer) {
    detailRows.push({ label: "Lock", value: def.lockLayer });
  }
  cardFullscreen.show({
    verseId: def.id,
    reference: def.reference,
    category: def.category,
    speakSeconds: speak,
    metaLine: `${def.category} · ${def.kind}`,
    keywords: kws.map((k) => ({
      id: k.id,
      label: k.label,
      reminder: k.reminder,
    })),
    textKjv: def.textKjv,
    proclaimText: def.proclaimText,
    effectSummary: def.effectSummary,
    detailRows,
    studyLayout: true,
  });
}

function isMissionCleared(save: CampaignSave, missionId: string): boolean {
  return DIFFS.some((d) => isDifficultyCleared(save, missionId, d));
}

function isCampaignStoryComplete(
  save: CampaignSave,
  camp: CampaignDef,
): boolean {
  const missions = camp.missions;
  if (!missions?.length) return false;
  const finaleId =
    camp.finaleMissionId ?? missions[missions.length - 1]!.id;
  return isMissionCleared(save, finaleId);
}

function campaignHasProgress(save: CampaignSave, camp: CampaignDef): boolean {
  const missions = camp.missions;
  if (!missions?.length) return false;
  if (missions.some((m) => isMissionCleared(save, m.id))) return true;
  return missions.some(
    (m) => m.index > 0 && isMissionUnlocked(save, m.index, camp.id),
  );
}

/**
 * Campaign the player would "continue" — for library selection only
 * (does not open the stage map).
 */
function pickContinueCampaignId(save: CampaignSave): string {
  const playable = CAMPAIGNS.filter(
    (c) => c.status === "playable" && (c.missions?.length ?? 0) > 0,
  );
  for (const c of playable) {
    if (!isCampaignStoryComplete(save, c) && campaignHasProgress(save, c)) {
      return c.id;
    }
  }
  for (const c of playable) {
    if (!isCampaignStoryComplete(save, c)) return c.id;
  }
  const last = readLastCampaignId();
  if (last && campaignById(last)) return last;
  return playable[playable.length - 1]?.id ?? defaultCampaignId();
}

function nextUnclearedMission(
  save: CampaignSave,
  missions: readonly MissionDef[],
  campaignId: string,
): MissionDef {
  const unlocked = missions.filter((m) =>
    isMissionUnlocked(save, m.index, campaignId),
  );
  return (
    unlocked.find((m) => !isMissionCleared(save, m.id)) ??
    unlocked[unlocked.length - 1] ??
    missions[0]!
  );
}

function storyArtUrl(src: string): string {
  return src.startsWith("/") ? src : `/art/story/${src}`;
}

function readLastCampaignId(): string | null {
  try {
    return localStorage.getItem(LAST_CAMPAIGN_KEY);
  } catch {
    return null;
  }
}

function writeLastCampaignId(id: string): void {
  try {
    localStorage.setItem(LAST_CAMPAIGN_KEY, id);
  } catch {
    /* ignore */
  }
}

export class CampaignHub {
  private root: HTMLElement;
  private headerTextEl: HTMLElement;
  private bodyEl: HTMLElement;
  private listEl: HTMLElement;
  private detailEl: HTMLElement;
  private ownedEl: HTMLElement;
  private hintEl: HTMLElement;
  private onStart: (mission: MissionDef, difficulty: CampaignDifficulty) => void;
  private onViewChange:
    | ((state: {
        view: HubView;
        campaignId: string;
        selectMissionId: string | null;
      }) => void)
    | null;
  private view: HubView = "library";
  private campaignId: string = defaultCampaignId();
  private selectedId: string | null = null;
  private selectedDiff: CampaignDifficulty = "standard";
  private save: CampaignSave | null = null;

  constructor(
    root: HTMLElement,
    handlers: {
      onStart: (mission: MissionDef, difficulty: CampaignDifficulty) => void;
      /** Fired when library ↔ stage map changes (for URL + chrome). */
      onViewChange?: (state: {
        view: HubView;
        campaignId: string;
        selectMissionId: string | null;
      }) => void;
    },
  ) {
    this.root = root;
    this.onStart = handlers.onStart;
    this.onViewChange = handlers.onViewChange ?? null;

    const last = readLastCampaignId();
    if (last && campaignById(last)) this.campaignId = last;

    this.root.classList.add("campaign-overlay");
    this.root.innerHTML = `
      <div class="campaign-shell" role="document">
        <header class="campaign-header">
          <div class="campaign-header-text" data-header-text>
            <p class="proto-label"><a href="/">← Main menu</a></p>
            <p class="campaign-kicker">Campaign library</p>
            <h2>Choose your path</h2>
            <p class="campaign-lead">Story arcs under pressure. Endless modes live under Free play on the main menu.</p>
          </div>
        </header>
        <div class="campaign-body" data-body>
          <nav class="campaign-list" data-list aria-label="Campaigns"></nav>
          <div class="campaign-detail" data-detail>
            <p class="muted">Select a campaign.</p>
          </div>
        </div>
        <footer class="campaign-footer">
          <p class="campaign-owned" data-owned></p>
          <p class="campaign-hint" data-hint>Sword of the Spirit · Proclaim</p>
        </footer>
      </div>
    `;

    this.headerTextEl = this.root.querySelector("[data-header-text]")!;
    this.bodyEl = this.root.querySelector("[data-body]")!;
    this.listEl = this.root.querySelector("[data-list]")!;
    this.detailEl = this.root.querySelector("[data-detail]")!;
    this.ownedEl = this.root.querySelector("[data-owned]")!;
    this.hintEl = this.root.querySelector("[data-hint]")!;

    this.root.addEventListener("click", (e) => this.onClick(e));
  }

  private onClick(e: MouseEvent): void {
    const t = e.target as HTMLElement;

    const cardBtn = t.closest("[data-study]") as HTMLElement | null;
    if (cardBtn?.dataset.study) {
      e.preventDefault();
      e.stopPropagation();
      const def = verseById(cardBtn.dataset.study);
      if (def) openVerseStudy(def);
      return;
    }

    const act = t.closest("[data-act]") as HTMLElement | null;
    if (!act) return;
    const kind = act.dataset.act;

    if (kind === "library") {
      this.view = "library";
      this.selectedId = null;
      // Keep the campaign that was open on the map selected in the list
      this.paint();
      this.emitViewChange();
      return;
    }

    // List click: select only — show campaign detail, do not open stages
    if (kind === "select-campaign") {
      const id = act.dataset.id;
      if (!id || !campaignById(id)) return;
      this.campaignId = id;
      writeLastCampaignId(id);
      this.view = "library";
      this.selectedId = null;
      this.paintLibrarySelection();
      this.renderLibraryDetail();
      this.scheduleScrollSelectedIntoView(true);
      this.emitViewChange();
      return;
    }

    // Explicit "Open campaign" — enter the stage map
    if (kind === "open-campaign") {
      const id = act.dataset.id ?? this.campaignId;
      if (!id) return;
      const camp = campaignById(id);
      if (!camp) return;
      this.campaignId = id;
      writeLastCampaignId(id);
      if (camp.status === "playable" && camp.missions?.length) {
        this.view = "missions";
        if (this.save) {
          this.selectedId = nextUnclearedMission(
            this.save,
            camp.missions,
            camp.id,
          ).id;
        }
      } else {
        this.view = "library";
        this.selectedId = null;
      }
      this.paint();
      this.emitViewChange();
      return;
    }

    if (kind === "mission") {
      const id = act.dataset.id;
      const missions = this.currentMissions();
      const campId = this.currentCampaign().id;
      if (
        id &&
        this.save &&
        isMissionUnlocked(
          this.save,
          missions.find((m) => m.id === id)?.index ?? 99,
          campId,
        )
      ) {
        this.selectedId = id;
        this.paintListSelection();
        this.scrollSelectedIntoView(true);
        this.renderMissionDetail();
        this.emitViewChange();
      }
      return;
    }

    if (kind === "diff") {
      const d = act.dataset.diff as CampaignDifficulty;
      if (DIFFS.includes(d)) {
        this.selectedDiff = d;
        this.renderMissionDetail();
      }
      return;
    }

    if (kind === "start") {
      const mission = this.currentMissions().find(
        (m) => m.id === this.selectedId,
      );
      if (mission) this.onStart(mission, this.selectedDiff);
    }
  }

  private currentCampaign(): CampaignDef {
    return campaignById(this.campaignId) ?? CAMPAIGNS[0]!;
  }

  private currentMissions(): readonly MissionDef[] {
    return this.currentCampaign().missions ?? [];
  }

  show(): void {
    this.root.classList.remove("hidden");
    document.body.classList.add("campaign-open");
    this.scheduleScrollSelectedIntoView(false);
  }

  hide(): void {
    this.root.classList.add("hidden");
    document.body.classList.remove("campaign-open");
  }

  isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }

  getView(): HubView {
    return this.view;
  }

  getCampaignId(): string {
    return this.campaignId;
  }

  getSelectedMissionId(): string | null {
    return this.selectedId;
  }

  private emitViewChange(): void {
    this.onViewChange?.({
      view: this.view,
      campaignId: this.campaignId,
      selectMissionId: this.selectedId,
    });
  }

  /**
   * Open hub. Prefer library so the player can pick a campaign;
   * pass `missions` to jump straight into the last campaign's map.
   * Optional `selectMissionId` focuses a stage (e.g. failed current or next after win).
   */
  render(
    save: CampaignSave,
    opts?: {
      view?: HubView;
      campaignId?: string;
      /** Prefer this stage when opening the missions map (must be unlocked). */
      selectMissionId?: string;
    },
  ): void {
    this.save = save;
    if (opts?.view) this.view = opts.view;
    else this.view = "library";

    if (opts?.campaignId && campaignById(opts.campaignId)) {
      this.campaignId = opts.campaignId;
      writeLastCampaignId(this.campaignId);
    } else if (this.view === "library") {
      // Hub opened on the list: select the continue campaign, do not open stages
      this.campaignId = pickContinueCampaignId(save);
      writeLastCampaignId(this.campaignId);
    }

    const camp = this.currentCampaign();
    if (this.view === "missions" && camp.status === "playable" && camp.missions) {
      const preferred = opts?.selectMissionId;
      const preferredMission = preferred
        ? camp.missions.find((m) => m.id === preferred)
        : undefined;
      if (
        preferredMission &&
        isMissionUnlocked(save, preferredMission.index, camp.id)
      ) {
        this.selectedId = preferredMission.id;
      } else {
        this.selectedId = nextUnclearedMission(
          save,
          camp.missions,
          camp.id,
        ).id;
      }
    } else if (this.view === "library") {
      // Library = stages not open; mission selection cleared
      this.selectedId = null;
    }

    this.paint();
    this.emitViewChange();
    if (this.isOpen()) {
      this.scheduleScrollSelectedIntoView(false);
    }
  }

  private paint(): void {
    if (this.view === "library") {
      this.paintLibrary();
    } else {
      this.paintMissions();
    }
  }

  private paintLibrary(): void {
    this.bodyEl.classList.add("campaign-body-library");
    this.bodyEl.classList.remove("campaign-body-missions");
    this.listEl.setAttribute("aria-label", "Campaigns");

    this.headerTextEl.innerHTML = `
      <nav class="campaign-crumbs" aria-label="Breadcrumb">
        <a href="/">Main menu</a>
        <span class="campaign-crumb-sep" aria-hidden="true">/</span>
        <span class="campaign-crumb-here" aria-current="page">Campaigns</span>
      </nav>
      <p class="campaign-kicker">Campaign library</p>
      <h2>Choose your path</h2>
      <p class="campaign-lead">Select a campaign to read its path. Open campaign when you are ready to fight.</p>
    `;

    this.listEl.replaceChildren();
    for (const c of CAMPAIGNS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "campaign-mission-btn campaign-book-btn";
      btn.dataset.act = "select-campaign";
      btn.dataset.id = c.id;
      if (c.id === this.campaignId) btn.classList.add("is-selected");
      if (c.status === "coming_soon") btn.classList.add("is-coming-soon");

      const statusLabel =
        c.status === "playable"
          ? `${c.stageCount ?? "?"} stages`
          : "Coming soon";
      const complete =
        this.save && c.status === "playable"
          ? isCampaignStoryComplete(this.save, c)
          : false;

      btn.innerHTML = `
        <span class="cm-index cm-book">${c.status === "playable" ? (complete ? "✓" : "▶") : "…"}</span>
        <span class="cm-text">
          <span class="cm-name">${c.title}</span>
          <span class="cm-meta">
            <span class="cm-chips">
              <span class="cm-meta-muted">${complete ? "Cleared" : statusLabel}</span>
              ${c.focus ? `<span class="cm-focus">${c.focus}</span>` : ""}
            </span>
          </span>
        </span>
      `;
      this.listEl.appendChild(btn);
    }

    const kinds = this.save ? uniqueIds(this.save.ownedCounts).length : 0;
    const copies = this.save ? totalCount(this.save.ownedCounts) : 0;
    this.ownedEl.textContent =
      copies === kinds
        ? `Arsenal · ${kinds} verses owned`
        : `Arsenal · ${kinds} verses · ${copies} copies`;
    this.hintEl.textContent =
      "Select a campaign for details · Open campaign for the stage map";

    this.renderLibraryDetail();
  }

  private paintLibrarySelection(): void {
    for (const el of this.listEl.querySelectorAll(".campaign-book-btn")) {
      const btn = el as HTMLElement;
      btn.classList.toggle("is-selected", btn.dataset.id === this.campaignId);
    }
  }

  private renderLibraryDetail(): void {
    const camp = this.currentCampaign();
    const save = this.save;
    this.detailEl.replaceChildren();

    // Campaign identity art (not a stage panel)
    const cover = document.createElement("div");
    cover.className = "campaign-cover";
    if (camp.coverArt) {
      const img = document.createElement("img");
      img.src = storyArtUrl(camp.coverArt);
      img.alt = "";
      img.decoding = "async";
      img.loading = "lazy";
      img.onerror = () => {
        cover.classList.add("is-placeholder");
        cover.replaceChildren();
        cover.textContent = camp.title;
      };
      cover.appendChild(img);
    } else {
      cover.classList.add("is-placeholder");
      cover.innerHTML = `<span class="campaign-cover-title">${camp.title}</span>`;
    }
    this.detailEl.appendChild(cover);

    const head = document.createElement("div");
    head.className = "campaign-detail-head";
    const complete = save ? isCampaignStoryComplete(save, camp) : false;
    head.innerHTML = `
      <h3>${camp.title}</h3>
      <p class="campaign-scripture">${camp.kicker}</p>
      <p class="campaign-blurb">${camp.lead}</p>
      ${
        camp.focus
          ? `<p class="campaign-lie"><span class="campaign-lie-label">Focus</span> ${camp.focus}</p>`
          : ""
      }
      ${
        complete
          ? `<p class="campaign-complete-badge">Story complete on this device</p>`
          : ""
      }
      <p class="muted campaign-attribution">${camp.attribution}</p>
    `;
    this.detailEl.appendChild(head);

    // Unique finale card reward (signature verse), when the campaign has one
    if (camp.signatureVerseId) {
      const sig = document.createElement("section");
      sig.className = "campaign-signature";
      const label = document.createElement("p");
      label.className = "campaign-section-label";
      label.textContent = "Finale reward · unique verse";
      sig.appendChild(label);
      const lead = document.createElement("p");
      lead.className = "muted campaign-signature-lead";
      lead.textContent =
        "Granted the first time you clear the final stage (any difficulty).";
      sig.appendChild(lead);
      const grid = document.createElement("div");
      grid.className = "campaign-card-grid";
      const owned = save
        ? isOwned(save, camp.signatureVerseId)
        : false;
      const copies = save
        ? ownedCount(save.ownedCounts, camp.signatureVerseId)
        : 0;
      grid.appendChild(
        this.makeCardTile(camp.signatureVerseId, {
          copies: copies > 0 ? copies : undefined,
          owned,
          ownedBadge: owned ? "Owned" : "Reward",
          compact: true,
        }),
      );
      sig.appendChild(grid);
      this.bindCardPeek(grid);
      this.detailEl.appendChild(sig);
    }

    const actions = document.createElement("div");
    actions.className = "campaign-detail-actions";

    if (camp.status === "playable" && camp.missions?.length) {
      const open = document.createElement("button");
      open.type = "button";
      open.className = "btn primary campaign-start";
      open.dataset.act = "open-campaign";
      open.dataset.id = camp.id;
      open.textContent = "Open campaign";
      actions.appendChild(open);
    } else {
      const soon = document.createElement("button");
      soon.type = "button";
      soon.className = "btn secondary campaign-start";
      soon.disabled = true;
      soon.textContent = "Coming soon";
      actions.appendChild(soon);
      const note = document.createElement("p");
      note.className = "muted campaign-coming-note";
      note.textContent =
        "This arc is on the roadmap. Clear another campaign first, or check back after the next content drop.";
      this.detailEl.appendChild(note);
    }

    this.detailEl.appendChild(actions);
  }

  private paintMissions(): void {
    const camp = this.currentCampaign();
    const missions = this.currentMissions();
    if (!missions.length || !this.save) {
      this.view = "library";
      this.paintLibrary();
      return;
    }

    this.bodyEl.classList.remove("campaign-body-library");
    this.bodyEl.classList.add("campaign-body-missions");
    this.listEl.setAttribute("aria-label", "Stages");

    this.headerTextEl.innerHTML = `
      <nav class="campaign-crumbs" aria-label="Breadcrumb">
        <a href="/">Main menu</a>
        <span class="campaign-crumb-sep" aria-hidden="true">/</span>
        <button type="button" class="linkish" data-act="library">Campaigns</button>
        <span class="campaign-crumb-sep" aria-hidden="true">/</span>
        <span class="campaign-crumb-here" aria-current="page">${camp.title}</span>
      </nav>
      <p class="campaign-kicker">${camp.kicker}</p>
      <h2>${camp.title}</h2>
      <p class="campaign-lead">${camp.lead}</p>
    `;

    const campId = camp.id;
    const next = nextUnclearedMission(this.save, missions, campId);
    if (!this.selectedId || !missions.some((m) => m.id === this.selectedId)) {
      this.selectedId = next.id;
    }

    this.listEl.replaceChildren();
    for (const m of missions) {
      const unlocked = isMissionUnlocked(this.save, m.index, campId);
      const cleared = isMissionCleared(this.save, m.id);
      const isNext = unlocked && !cleared && m.id === next.id;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "campaign-mission-btn";
      btn.dataset.act = "mission";
      btn.dataset.id = m.id;
      btn.disabled = !unlocked;
      if (m.id === this.selectedId) btn.classList.add("is-selected");
      if (isNext) btn.classList.add("is-next");

      const clears = DIFFS.filter((d) =>
        isDifficultyCleared(this.save!, m.id, d),
      );
      const clearChips = clears.length
        ? clears
            .map(
              (d) =>
                `<span class="cm-chip cm-chip-${d}" title="${DIFF_LABEL[d]} cleared">${DIFF_LABEL[d]} ✓</span>`,
            )
            .join("")
        : isNext
          ? `<span class="cm-meta-next">Next up</span>`
          : `<span class="cm-meta-muted">Not cleared</span>`;

      btn.innerHTML = `
        <span class="cm-index">${m.index + 1}</span>
        <span class="cm-text">
          <span class="cm-name">${m.name}</span>
          <span class="cm-meta">${
            unlocked
              ? `<span class="cm-chips">${clearChips}</span>`
              : "Locked — clear the previous stage"
          }</span>
        </span>
      `;
      this.listEl.appendChild(btn);
    }

    const kinds = uniqueIds(this.save.ownedCounts).length;
    const copies = totalCount(this.save.ownedCounts);
    this.ownedEl.textContent =
      copies === kinds
        ? `Arsenal · ${kinds} verses owned`
        : `Arsenal · ${kinds} verses · ${copies} copies`;
    this.hintEl.textContent = `${camp.attribution} · Click a card to study before battle.`;

    this.renderMissionDetail();
  }

  private paintListSelection(): void {
    for (const el of this.listEl.querySelectorAll(".campaign-mission-btn")) {
      const btn = el as HTMLElement;
      btn.classList.toggle("is-selected", btn.dataset.id === this.selectedId);
    }
  }

  private scheduleScrollSelectedIntoView(smooth: boolean): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.scrollSelectedIntoView(smooth));
    });
  }

  private scrollSelectedIntoView(smooth: boolean): void {
    if (!this.isOpen()) return;
    let btn: HTMLElement | null = null;
    if (this.view === "missions" && this.selectedId) {
      btn = this.listEl.querySelector(
        `.campaign-mission-btn[data-id="${CSS.escape(this.selectedId)}"]`,
      ) as HTMLElement | null;
    } else if (this.view === "library" && this.campaignId) {
      btn = this.listEl.querySelector(
        `.campaign-book-btn[data-act="select-campaign"][data-id="${CSS.escape(this.campaignId)}"]`,
      ) as HTMLElement | null;
    }
    if (!btn) return;

    const list = this.listEl;
    const listRect = list.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const behavior: ScrollBehavior = smooth ? "smooth" : "auto";

    const horizontal = list.scrollWidth > list.clientWidth + 1;
    const vertical = list.scrollHeight > list.clientHeight + 1;
    if (!horizontal && !vertical) return;

    if (horizontal) {
      const delta =
        btnRect.left +
        btnRect.width / 2 -
        (listRect.left + listRect.width / 2);
      const max = Math.max(0, list.scrollWidth - list.clientWidth);
      const left = Math.max(0, Math.min(max, list.scrollLeft + delta));
      list.scrollTo({ left, behavior });
      return;
    }

    const delta =
      btnRect.top +
      btnRect.height / 2 -
      (listRect.top + listRect.height / 2);
    const max = Math.max(0, list.scrollHeight - list.clientHeight);
    const top = Math.max(0, Math.min(max, list.scrollTop + delta));
    list.scrollTo({ top, behavior });
  }

  private renderMissionDetail(): void {
    const campaignSave = this.save;
    if (!campaignSave) return;

    const mission = this.currentMissions().find((m) => m.id === this.selectedId);
    if (!mission) {
      this.detailEl.innerHTML = `<p class="muted">Select a mission.</p>`;
      return;
    }

    const unlocked = isMissionUnlocked(
      campaignSave,
      mission.index,
      this.currentCampaign().id,
    );
    const deck = resolveMissionLoadout(mission, campaignSave);
    const deckCounts = countsFromIds(deck);
    this.detailEl.replaceChildren();

    const head = document.createElement("div");
    head.className = "campaign-detail-head";
    const story = mission.story;
    head.innerHTML = `
      <h3>${mission.name}</h3>
      ${
        story
          ? `<p class="campaign-scripture">${story.scriptureRef}</p>
             <p class="campaign-lie"><span class="campaign-lie-label">The lie</span> ${story.lie}</p>`
          : ""
      }
      <p class="campaign-blurb">${mission.blurb}</p>
    `;
    this.detailEl.appendChild(head);

    const diffPanel = document.createElement("div");
    diffPanel.className = "campaign-diff-panel";
    diffPanel.innerHTML = `<div class="campaign-diff-label">Difficulty</div>`;
    const diffRow = document.createElement("div");
    diffRow.className = "campaign-diff-row";
    for (const d of DIFFS) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "campaign-diff-btn";
      b.dataset.act = "diff";
      b.dataset.diff = d;
      if (d === this.selectedDiff) b.classList.add("is-selected");
      const cleared = isDifficultyCleared(campaignSave, mission.id, d);
      b.innerHTML = `${DIFF_LABEL[d]}${cleared ? " ✓" : ""}`;
      diffRow.appendChild(b);
    }
    diffPanel.appendChild(diffRow);
    this.detailEl.appendChild(diffPanel);

    const loadSec = document.createElement("section");
    loadSec.className = "campaign-card-section";
    loadSec.innerHTML = `
      <div class="campaign-section-head">
        <h4>Loadout</h4>
        <p>${loadoutLabel(mission, deck)} · click a card to study</p>
      </div>
    `;
    const loadGrid = document.createElement("div");
    loadGrid.className = "campaign-card-grid campaign-card-grid-compact";
    loadGrid.setAttribute("aria-label", "Mission loadout");
    for (const id of uniqueIds(deckCounts)) {
      loadGrid.appendChild(
        this.makeCardTile(id, {
          copies: deckCounts[id],
          owned: isOwned(campaignSave, id),
          compact: true,
        }),
      );
    }
    loadSec.appendChild(loadGrid);
    this.bindCardPeek(loadGrid);
    this.detailEl.appendChild(loadSec);

    const rewSec = document.createElement("section");
    rewSec.className = "campaign-card-section";
    rewSec.innerHTML = `
      <div class="campaign-section-head">
        <h4>Mission rewards</h4>
        <p>Pick one on Easy/Standard (+1 copy) · Hard grants +1 of each</p>
      </div>
    `;
    const rewGrid = document.createElement("div");
    rewGrid.className = "campaign-card-grid campaign-card-grid-rewards";
    rewGrid.setAttribute("aria-label", "Mission rewards");
    for (const id of mission.rewardCards) {
      const n = ownedCount(campaignSave.ownedCounts, id);
      rewGrid.appendChild(
        this.makeCardTile(id, {
          copies: n > 0 ? n : undefined,
          owned: n > 0,
          ownedBadge: n > 0 ? `Owned ×${n} · +1` : "New",
        }),
      );
    }
    rewSec.appendChild(rewGrid);
    this.bindCardPeek(rewGrid);
    this.detailEl.appendChild(rewSec);

    const arsenal = document.createElement("details");
    arsenal.className = "campaign-card-section campaign-arsenal";
    const ownedKinds = uniqueIds(campaignSave.ownedCounts);
    const ownedCopies = totalCount(campaignSave.ownedCounts);
    const arSummary = document.createElement("summary");
    arSummary.className = "campaign-arsenal-summary";
    arSummary.innerHTML = `
      <span class="campaign-arsenal-title">Your arsenal</span>
      <span class="campaign-arsenal-meta">${ownedKinds.length} verses · ${ownedCopies} copies</span>
    `;
    arsenal.appendChild(arSummary);
    const arBody = document.createElement("div");
    arBody.className = "campaign-arsenal-body";
    const arHint = document.createElement("p");
    arHint.className = "campaign-arsenal-hint";
    arHint.textContent = "Hover to peek · tap to study";
    arBody.appendChild(arHint);
    const arGrid = document.createElement("div");
    arGrid.className = "campaign-card-grid campaign-card-grid-compact";
    arGrid.setAttribute("aria-label", "Owned arsenal");
    if (ownedKinds.length === 0) {
      const empty = document.createElement("p");
      empty.className = "muted campaign-empty";
      empty.textContent =
        "No verses in your arsenal yet — clear stages to earn rewards.";
      arGrid.appendChild(empty);
    } else {
      for (const id of ownedKinds) {
        arGrid.appendChild(
          this.makeCardTile(id, {
            copies: ownedCount(campaignSave.ownedCounts, id),
            owned: true,
            compact: true,
          }),
        );
      }
    }
    arBody.appendChild(arGrid);
    arsenal.appendChild(arBody);
    this.bindCardPeek(arGrid);
    this.detailEl.appendChild(arsenal);

    const actions = document.createElement("div");
    actions.className = "campaign-detail-actions";
    const start = document.createElement("button");
    start.type = "button";
    start.className = "btn primary campaign-start";
    start.dataset.act = "start";
    start.disabled = !unlocked;
    start.textContent = `Begin · ${DIFF_LABEL[this.selectedDiff]}`;
    actions.appendChild(start);
    this.detailEl.appendChild(actions);
  }

  private makeCardTile(
    id: string,
    opts: {
      copies?: number;
      owned?: boolean;
      ownedBadge?: string;
      compact?: boolean;
    },
  ): HTMLElement {
    const def = verseById(id);
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className =
      "campaign-card-tile" + (opts.compact ? " is-compact" : "");
    tile.dataset.study = id;
    if (opts.owned === false) tile.classList.add("is-unowned");

    if (def) {
      const face = createVerseCardFace({
        verseId: def.id,
        reference: def.reference,
        category: def.category,
        speakSeconds: def.speakSeconds ?? 2.5,
        metaLine: `${def.category} · ${def.kind}`,
      });
      face.classList.add("campaign-face");
      tile.appendChild(face);
    } else {
      tile.textContent = id;
    }

    if (opts.copies !== undefined && opts.copies > 0) {
      const q = document.createElement("span");
      q.className = "campaign-card-qty";
      q.textContent = `×${opts.copies}`;
      tile.appendChild(q);
    }
    if (opts.ownedBadge) {
      const b = document.createElement("span");
      b.className = "campaign-owned-badge";
      b.textContent = opts.ownedBadge;
      tile.appendChild(b);
    }
    return tile;
  }

  private bindCardPeek(grid: HTMLElement): void {
    let peekEl: HTMLElement | null = null;

    const clearPeek = (): void => {
      if (!peekEl) return;
      peekEl.classList.remove("is-peek");
      peekEl = null;
    };

    const setPeek = (tile: HTMLElement | null): void => {
      if (tile === peekEl) return;
      clearPeek();
      if (!tile || !grid.contains(tile)) return;
      peekEl = tile;
      tile.classList.add("is-peek");
    };

    const tileFromPoint = (x: number, y: number): HTMLElement | null => {
      const stack = document.elementsFromPoint(x, y);
      for (const node of stack) {
        if (!(node instanceof Element)) continue;
        const tile = node.closest(".campaign-card-tile");
        if (tile instanceof HTMLElement && grid.contains(tile)) return tile;
      }
      return null;
    };

    grid.addEventListener("pointermove", (e) => {
      setPeek(tileFromPoint(e.clientX, e.clientY));
    });
    grid.addEventListener("pointerdown", (e) => {
      if (e.pointerType === "touch" || e.pointerType === "pen") {
        setPeek(tileFromPoint(e.clientX, e.clientY));
      }
    });
    grid.addEventListener("pointerleave", () => clearPeek());
  }
}
